import * as swcHelpers from '@swc/helpers';
import { getIncludedVatAmount } from '@grandvisionhq/analytics';
import { isDefined, getLocalizedValue, getAttrValue, getLocalizedAttrValue, getJoinedLocalizedAttrValues } from '@grandvisionhq/utils';
import { isLightProductModel } from '../../../helpers.js';
import { createAltTag } from '../../../utils/create-alt-tag.js';
import { getDiscount } from '../../../utils/get-discount.js';
import { getDiscountDescription } from '../../../utils/get-discount-description.js';
import { getProductTag } from '../../../utils/get-product-tag.js';

const mapProductProjectionToListingProduct = (product, intl)=>{
    var ref, ref1, ref2, ref3, ref4, ref5, ref6, ref7, ref8;
    const { getLabel , locale  } = intl;
    const labels = {
        lens: getLabel(`products.unit.contactLens`),
        lenses: getLabel(`products.unit.contactLenses`),
        perBox: getLabel(`products.perBox`)
    };
    const masterVariant = mapVariantInfo(product.masterVariant, locale, labels);
    const variants = product.variants.map((variant)=>mapVariantInfo(variant, locale, labels)
    ).filter(isDefined);
    if (!masterVariant) {
        //We should always have a mastervariant
        console.error(`ERROR: No masterVariant found for product.slug: ${product.slug.en}`);
        return undefined;
    }
    if (!((ref = product.productType.obj) === null || ref === void 0 ? void 0 : ref.name) || ((ref1 = product.productType.obj) === null || ref1 === void 0 ? void 0 : ref1.name) === 'Additional-cart-costs') {
        var ref9;
        console.error(`ERROR: No valid productType received for product.slug: ${product.slug.en}`);
        console.error(`ERROR: productType: ${(ref9 = product.productType.obj) === null || ref9 === void 0 ? void 0 : ref9.name}`);
        return undefined;
    }
    const productType = product.productType.obj.name;
    const includedVatAmount = getIncludedVatAmount(product === null || product === void 0 ? void 0 : (ref2 = product.taxCategory) === null || ref2 === void 0 ? void 0 : (ref3 = ref2.obj) === null || ref3 === void 0 ? void 0 : ref3.rates);
    const categoryName = (ref4 = product.categories) === null || ref4 === void 0 ? void 0 : (ref5 = ref4[0]) === null || ref5 === void 0 ? void 0 : (ref6 = ref5.obj) === null || ref6 === void 0 ? void 0 : ref6.name;
    const category = categoryName ? getLocalizedValue(categoryName, 'en') : undefined;
    const isOnStock = isLightProductModel(product) ? product.isOnStock : undefined;
    const productOffer = isLightProductModel(product) ? product === null || product === void 0 ? void 0 : product.offering : undefined;
    const offering = productOffer ? {
        includesLenses: productOffer === null || productOffer === void 0 ? void 0 : productOffer.includesLenses
    } : undefined;
    const ratingsSummary = !isLightProductModel(product) ? {
        average: (ref7 = product.ratingsSummary) === null || ref7 === void 0 ? void 0 : ref7.average,
        reviewCount: (ref8 = product.ratingsSummary) === null || ref8 === void 0 ? void 0 : ref8.reviewCount
    } : undefined;
    return {
        category,
        includedVatAmount,
        isOnStock,
        masterVariant,
        name: {
            en: product.name.en,
            localized: getLocalizedValue(product.name, locale)
        },
        objectID: isLightProductModel(product) ? product.objectID : '',
        offering,
        productType,
        ratingsSummary,
        slug: getLocalizedValue(product.slug, locale),
        variants
    };
};
const mapVariantInfo = (variant, locale, labels)=>{
    var ref;
    const price = mapPrice(variant.prices, locale);
    const altTag = createAltTag({
        variant,
        locale,
        labels
    });
    if (!price) {
        //We should always have a valid price
        console.error(`ERROR: no valid price for variant with sku: ${variant.sku}`);
        return undefined;
    }
    const packageOption = getAttrValue(variant, 'packageOption') ? String(getAttrValue(variant, 'packageOption')) : undefined;
    const solutionType = getAttrValue(variant, 'solutionType') ? String(getAttrValue(variant, 'solutionType')) : undefined;
    const power = getAttrValue(variant, 'power') ? String(getAttrValue(variant, 'power')) : undefined;
    const packageQuantity = getAttrValue(variant, 'packageQuantity') ? Number(getAttrValue(variant, 'packageQuantity')) : undefined;
    const priceIncluded = getAttrValue(variant, 'priceIncluded');
    const priceIncludedAmount = priceIncluded && typeof priceIncluded === 'object' ? Number(priceIncluded.centAmount) : undefined;
    const isExclusiveBrand = isDefined(getAttrValue(variant, 'exclusiveBrand')) ? Boolean(getAttrValue(variant, 'exclusiveBrand')) : undefined;
    return {
        brand: {
            name: String(getAttrValue(variant, 'brand')),
            model: String(getAttrValue(variant, 'brandModel')),
            modelCode: String(getAttrValue(variant, 'brandModelCode'))
        },
        erpId: String(getAttrValue(variant, 'erpId')),
        description: getLocalizedAttrValue(variant, 'description', locale),
        frame: {
            color: {
                en: getJoinedLocalizedAttrValues(variant, 'frameColor', 'en'),
                localized: getJoinedLocalizedAttrValues(variant, 'frameColor', locale)
            },
            material: {
                en: getLocalizedAttrValue(variant, 'frameMaterial', 'en')
            },
            shape: {
                en: getLocalizedAttrValue(variant, 'frameShape', 'en')
            },
            type: {
                en: getLocalizedAttrValue(variant, 'frameType', 'en')
            }
        },
        faceShape: {
            en: getJoinedLocalizedAttrValues(variant, 'faceShape', 'en')
        },
        gender: {
            en: getJoinedLocalizedAttrValues(variant, 'gender', 'en')
        },
        lensColor: getJoinedLocalizedAttrValues(variant, 'lensColor', locale),
        lensType: {
            en: getLocalizedAttrValue(variant, 'lensType', 'en')
        },
        replacementFrequency: getLocalizedAttrValue(variant, 'replacementFrequency', locale),
        material: getLocalizedAttrValue(variant, 'material', locale),
        accessoryType: {
            en: getLocalizedAttrValue(variant, 'accessoryType', 'en'),
            localized: getLocalizedAttrValue(variant, 'accessoryType', locale)
        },
        accessoryColor: {
            en: getJoinedLocalizedAttrValues(variant, 'accessoryColor', 'en'),
            localized: getJoinedLocalizedAttrValues(variant, 'accessoryColor', locale)
        },
        contactLensCareType: getLocalizedAttrValue(variant, 'contactLensCareType', locale),
        isInsuranceReimbursable: Boolean(getAttrValue(variant, 'isInsuranceReimbursable')),
        power,
        priceIncludedAmount,
        packageOption,
        packageQuantity,
        solutionType,
        isExclusiveBrand,
        name: variant.name || '',
        sku: String(variant.sku),
        tag: getProductTag(variant, locale),
        images: (ref = variant.media) === null || ref === void 0 ? void 0 : ref.images.map((img)=>swcHelpers.objectSpread({}, img, {
                altTag
            })
        ),
        pricing: {
            bestPrice: variant.bestPrice,
            price,
            pricePerUnit: variant.pricePerUnit || undefined
        }
    };
};
const mapPrice = (prices = [], locale)=>{
    var ref, ref10, ref11, ref12, ref13, ref14, ref15;
    const price = prices[0];
    const discount = price === null || price === void 0 ? void 0 : (ref = price.discounted) === null || ref === void 0 ? void 0 : ref.value.centAmount;
    if (!price) {
        return undefined;
    }
    return {
        originalPrice: price.value.centAmount,
        currencyCode: price.value.currencyCode,
        isCentAmount: true,
        discount: discount ? {
            description: getDiscountDescription(prices, locale),
            price: discount,
            badge: getDiscount(prices, locale),
            validFrom: price === null || price === void 0 ? void 0 : (ref10 = price.discounted) === null || ref10 === void 0 ? void 0 : (ref11 = ref10.discount) === null || ref11 === void 0 ? void 0 : (ref12 = ref11.obj) === null || ref12 === void 0 ? void 0 : ref12.validFrom,
            validUntil: price === null || price === void 0 ? void 0 : (ref13 = price.discounted) === null || ref13 === void 0 ? void 0 : (ref14 = ref13.discount) === null || ref14 === void 0 ? void 0 : (ref15 = ref14.obj) === null || ref15 === void 0 ? void 0 : ref15.validUntil
        } : undefined
    };
};

export { mapProductProjectionToListingProduct };
//# sourceMappingURL=map-product-projection-to-listing-product.js.map
